import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import LoadingSpinner from "../LoadingSpinner";
import Grid from "../Grid";
import { typeValidation } from "../validations/stakeholderCreation";
import { getTextByValueAndKey } from "../../api/keyvalue";
import ButtonRenderer from "../renderer/ButtonRenderer";
import Dropdown from "../input/Dropdown";
import Input from "../input/Input";
import Button from "../input/Button";
import { ResizableScrollingTextRenderer } from "../renderer/StyleRenderer";
import { getStakeholderMandatoryFields } from "../../api/mandatoryFieldsApi";
import { stakeholderActions } from "../../store/mandatoryFields/stakeholder-slice";
import { useDispatch } from "react-redux";
import { AUTH_TYPES, FORMS, MODULES } from "../../const";
import { isNumber } from "../../helper";
import { stkActions } from "../../store/stakeholder/stk-slice";
import DeleteButtonRenderer from "../renderer/DeleteButtonRenderer";

const FORM = FORMS.STAKEHOLDER.TYPE.form;

const StakeholderTypes = forwardRef(
  ({ dropdown, isLoading, currentValues, editMode, setStkTypeList }, ref) => {
    const dispatch = useDispatch();

    const [currentErrors, setCurrentErrors] = useState({});

    const [types, setTypes] = useState([]);
    const [typeCount, setTypeCount] = useState(2);
    const [isAddLoading, setIsAddLoading] = useState(false);
    const [currentType, setCurrentType] = useState({});
    const [mandatoryFieldsLoading, setMandatoryFieldsLoading] = useState(false);
    const [deletedList, setDeletedList] = useState([]);

    const [buttonClicked, setButtonClicked] = useState(false);

    const [initialActive, setInitialActive] = useState(
          editMode ? currentValues?.empActive : 1,
        );

    const [stakeholderTypeData, setStakeholderTypeData] = useState({
      typType: null,
      typTypeValue: "",
      typDescription: "",
      typStatus: 2,
      typActive: 1,
      typActiveValue: "",
      stkUserEnable: false,
    });

    useEffect(() => {
      dispatch(stkActions.setStakeholderTypes(types.map((typ) => typ.typType)));
      setStkTypeList(types.map((typ) => typ.typType));
    }, [types]);

    useEffect(() => {
      (async () => {
        setMandatoryFieldsLoading(true);
        let fieldSet = []; //stakeholder mandatory fields
        let clusterFieldSet = []; //cluster mandatory fields
        let financeFieldSet = []; //finance mandatory fields
        let operationFieldSet = []; //operation mandatory fields
        let configurationFieldSet = []; //configuration mandatory fields
        let consignmentFieldSet = []; //consignments mandatory fields

        let contactForm = false;

        for (let type of types) {
          if (type.typType) {
            const fields = await getStakeholderMandatoryFields({
              fmaForm: "2",
              fmaType: type.typType,
            });
            fieldSet = new Set([...fieldSet, ...fields]); // Using Set to avoid duplicates in the array. (Set is a collection of unique values)

            if (!contactForm) {
              const contactMandatory = await getStakeholderMandatoryFields({
                fmaForm: "3",
                fmaType: type.typType,
              });

              if (contactMandatory && contactMandatory.length > 0) {
                const contactFormMandatory = contactMandatory.find(
                  (item) => item.fmaField === null,
                );

                contactForm = contactFormMandatory
                  ? contactFormMandatory.fmaRequired === 1
                  : false;
              }
            }

            const clusterFields = await getStakeholderMandatoryFields({
              fmaForm: "4",
              fmaType: type.typType,
            });
            clusterFieldSet = new Set([...clusterFieldSet, ...clusterFields]);

            const financeFields = await getStakeholderMandatoryFields({
              fmaForm: "5",
              fmaType: type.typType,
            });
            financeFieldSet = new Set([...financeFieldSet, ...financeFields]);

            const operationFields = await getStakeholderMandatoryFields({
              fmaForm: "6",
              fmaType: type.typType,
            });
            operationFieldSet = new Set([
              ...operationFieldSet,
              ...operationFields,
            ]);

            const consignmentFields = await getStakeholderMandatoryFields({
              fmaForm: "7",
              fmaType: type.typType,
            });
            consignmentFieldSet = new Set([
              ...consignmentFieldSet,
              ...consignmentFields,
            ]);

            const configurationFields = await getStakeholderMandatoryFields({
              fmaForm: "8",
              fmaType: type.typType,
            });
            configurationFieldSet = new Set([
              ...configurationFieldSet,
              ...configurationFields,
            ]);
          }
        }

        //stakesholder mandatory fields
        let mFieldsObj = {};
        for (let field of fieldSet) {
          mFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        //cluster mandatory fields
        let clusterFieldsObj = {};
        for (let field of clusterFieldSet) {
          clusterFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        //finance mandatory fields
        let financeFieldsObj = {};
        for (let field of financeFieldSet) {
          financeFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        //operation mandatory fields
        let operationFieldsObj = {};
        for (let field of operationFieldSet) {
          operationFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        //configuration mandatory fields
        let configurationFieldsObj = {};
        for (let field of configurationFieldSet) {
          configurationFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        //consignment Type mandatory fields
        let consignmentFieldsObj = {};
        for (let field of consignmentFieldSet) {
          consignmentFieldsObj[field.fmaField] = {
            field: field.fmaField,
            name: field.fmaText,
            required: field.fmaRequired === 1 ? true : false,
          };
        }

        dispatch(stakeholderActions.setStakeholderTypes(types));
        dispatch(stakeholderActions.setMandatoryFields(mFieldsObj));
        dispatch(stakeholderActions.setContactForm(contactForm));
        dispatch(stakeholderActions.setClusterFormFields(clusterFieldsObj));
        dispatch(stakeholderActions.setFinanceFormFields(financeFieldsObj));
        dispatch(stakeholderActions.setOperationFormFields(operationFieldsObj));
        dispatch(
          stakeholderActions.setConfigurationFormFields(configurationFieldsObj),
        );
        dispatch(
          stakeholderActions.setConsignmentTypeFormFields(consignmentFieldsObj),
        );
        setMandatoryFieldsLoading(false);
      })();
    }, [types]);

    useEffect(() => {
      (async () => {
        if (editMode) {
          let currentTypes = [];
          let id = 2;
          for (let currentValue of currentValues) {
            currentTypes.push({
              ...currentValue,
              typStakeholder: currentValue.typStakeholderId,
              id,
            });
            ++id;
          }
          setTypeCount(id);
          setTypes(currentTypes);
        }
      })();
    }, [editMode, currentValues]);

    useEffect(() => {
      if (buttonClicked) {
        setStakeholderTypeData({
          typType: null,
          typTypeValue: "",
          typDescription: "",
          typActive: 1,
          typStatus: 1,
          typActiveValue: "",
        });

        setButtonClicked(false);
        setCurrentType({});
      }
    }, [buttonClicked]);

    useEffect(() => {
      if (Object.keys(currentType).length > 0) {
        setStakeholderTypeData({
          ...currentType,
          typTypeValue: "",
          typActiveValue: "",
        });
      }
    }, [editMode, currentType]);

    const getUpdateState = () => {
      const updateState = {
        modified: [],
        unmodified: [],
        created: [],
        deleted: deletedList,
      };

      types.forEach((typ) => {
        if (typ.typId && typ.typId !== null) {
          if (typ.modified) {
            updateState.modified.push(typ);
          } else {
            updateState.unmodified.push(typ);
          }
        } else {
          updateState.created.push(typ);
        }
      });

      return updateState;
    };

    useImperativeHandle(ref, () => ({
      getData: () => {
        if (editMode) {
          return getUpdateState();
        } else {
          return types;
        }
      },
      isDeletedActive: () =>
        types.typActive === 2 && initialActive !== 2,
      resetForm: () => {
        setButtonClicked(true);
        setTypes([]);
      },
      validateForm: () => {
        if (editMode) {
          const typeData = getUpdateState();
          const modifiedTypeData = typeData.modified;
          const newTypeData = typeData.created;
          const unModifiedData = typeData.unmodified;
          return (
            modifiedTypeData.length +
              newTypeData.length +
              unModifiedData.length <=
            0
          );
        } else {
          return types.length <= 0;
        }
      },
    }));

    const onAddClick = async () => {
      setIsAddLoading(true);

      // Get errors by runing the validator.
      const typeErrors = typeValidation({
        ...stakeholderTypeData,
      });

      // If no erros go forward with the adding or saving process.
      if (Object.keys(typeErrors).length === 0) {
        // Getting text values for the grid.
        const typTypeValue = await getTextByValueAndKey(
          "stakeholder_type",
          stakeholderTypeData.typType,
        );
        const typActiveValue = await getTextByValueAndKey(
          "active",
          stakeholderTypeData.typActive,
        );
        // Check whether the item is not already saved in the db by checking the primary key (In this case typId).
        if (!stakeholderTypeData.typId || stakeholderTypeData.typId === null) {
          /*
                                                                                                    If the item is not saved in the db
                                                                                                    Check whether the item has an id field to identify whether the item is a new item
                                                                                                    or an item that is previously added to the grid and loaded to the form for editing by pressing the edit button.
                                                                                                    */
          if (!stakeholderTypeData.id || stakeholderTypeData.id === null) {
            // If item doesn't have an id then add as a new item or else replace the old item.
            setTypes((prevState) => [
              ...prevState,
              {
                ...stakeholderTypeData,
                id: typeCount,
                typTypeValue,
                typActiveValue,
              },
            ]);
            setTypeCount((prevState) => ++prevState);
          } else {
            // If the item has an id then replace the old item with the new item.
            setTypes((prevState) =>
              prevState.filter((item) => item.id !== stakeholderTypeData.id),
            );
            setTypes((prevState) => [
              ...prevState,
              {
                ...stakeholderTypeData,
                typTypeValue,
                typActiveValue,
              },
            ]);
          }
        } else {
          // If saved in the db modify the item and replace the item in the array.
          setTypes((prevState) =>
            prevState.filter(
              (item) => item.typId !== stakeholderTypeData.typId,
            ),
          );
          setTypes((prevState) => [
            ...prevState,
            {
              ...stakeholderTypeData,
              typTypeValue,
              typActiveValue,
              modified: true,
            },
          ]);
        }
        setButtonClicked(true);
      }

      setCurrentErrors(typeErrors);
      setIsAddLoading(false);
    };

    return (
      <>
        {isLoading || mandatoryFieldsLoading ? (
          <LoadingSpinner />
        ) : (
          <form
            className="ui form"
            style={{ overflowY: "auto", height: "75vh" }}
          >
            <div className="ui segment">
              <h4 className="ui dividing header">Stakeholder Types</h4>
              <Input
                label="Description"
                error={currentErrors.typDescription}
                editMode={editMode}
                module={MODULES.Master}
                form={FORM}
                field="typ_discription"
                resetError={() =>
                  setCurrentErrors((prevState) => {
                    const { typDescription: _, ...rest } = prevState;
                    return rest;
                  })
                }
                name="description"
                placeholder="Description"
                value={stakeholderTypeData.typDescription}
                onChange={(e) => {
                  setStakeholderTypeData((prevState) => ({
                    ...prevState,
                    typDescription: e.target.value,
                  }));
                }}
              />

              <div className="two fields">
                <Dropdown
                  required={true}
                  label="Type"
                  error={currentErrors.typType}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="typ_type"
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { typType: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="(Consignee/Customer/Transporter)"
                  options={dropdown.stakeTypeOptions}
                  style={{ height: "20px" }}
                  value={stakeholderTypeData.typType}
                  onChange={(e, data) => {
                    setStakeholderTypeData((prevState) => ({
                      ...prevState,
                      typType: data.value,
                    }));
                  }}
                />
                {/* <Dropdown
                  required={true}
                  label="Active"
                  error={currentErrors.typActive}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={FORM}
                  field="typ_active"
                  needDeletePermission={true}
                  resetError={() =>
                    setCurrentErrors((prevState) => {
                      const { typActive: _, ...rest } = prevState;
                      return rest;
                    })
                  }
                  placeholder="Active"
                  options={dropdown.activeOptions}
                  style={{ height: "20px" }}
                  value={stakeholderTypeData.typActive}
                  onChange={(e, data) => {
                    setStakeholderTypeData((prevState) => ({
                      ...prevState,
                      typActive: data.value,
                    }));
                  }}
                /> */}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  template="reset"
                  onClick={(e) => {
                    setButtonClicked(true);
                  }}
                />
                <Button
                  template="addOrSave"
                  authenticated={true}
                  editMode={editMode}
                  module={MODULES.Master}
                  form={`${FORM}`}
                  create={Object.keys(currentType).length === 0}
                  onClick={onAddClick}
                  isLoading={isAddLoading}
                />
              </div>
              <Grid
                heading="Stakeholder Types"
                config={[
                  { headerName: "Type", field: "typTypeValue" },
                  {
                    headerName: "Description",
                    field: "typDescription",
                    cellRenderer: ResizableScrollingTextRenderer,
                  },
                  { headerName: "Active", field: "typActiveValue" },
                  {
                    field: "Edit",
                    filter: true,
                    cellRenderer: ButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.CREATE,
                      clickHandler: () => {
                        setCurrentType(params.data);
                      },
                      label: "Edit",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode: editMode,
                    }),
                  },
                  {
                    field: "Delete",
                    filter: true,
                    cellRenderer: DeleteButtonRenderer,
                    cellRendererParams: (params) => ({
                      consider: AUTH_TYPES.DELETE,
                      clickHandler: () => {
                        if (params.data.typId && params.data.typId !== null) {
                          setDeletedList((prevState) => [
                            ...prevState,
                            params.data.typId,
                          ]);
                        }
                        setTypes((prevTypes) =>
                          prevTypes.filter(
                            (type) => type.id !== params.data.id,
                          ),
                        );
                      },
                      message:
                        "Are you sure you want to delete this Stakeholder Type?",
                      label: "Delete",
                      className: "ui red button",
                      authenticated: true,
                      form: `${FORM}`,
                      module: MODULES.Master,
                      editMode:
                        params.data.typId !== null &&
                        isNumber(params.data.typId),
                    }),
                  },
                ]}
                content={types}
              />
            </div>
          </form>
        )}
      </>
    );
  },
);

export default StakeholderTypes;
